import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getTrainingScenarioImageMetadata,
  invalidateTrainingEvalCatalogs,
  listTrainingScenarioImages,
} from "@/api";
import { resolveEvalLoadErrorMessage } from "@/modules/evaluation/public";

// 이 훅이 내부에서 쓰는 비동기 상태 네 가지.
type ListViewStatus = "idle" | "loading" | "success" | "error";
import {
  metadataToParamSpecs,
  paramDefaults,
  taskParamSpec,
  type EvalParamSpec,
  type EvalParamValue,
  type EvalParamValues,
} from "./evalParamSpecs";
import { rankImageTags, semverProbeCandidates, type RankedImageTag } from "./imageTagRanking";
import { suggestTaskOption } from "./taskSuggestion";

export type { RankedImageTag } from "./imageTagRanking";

// 평가 이미지 태그와 조정 가능한 파라미터 상태를 소유한다. 이 훅은 다음을 한다:
//   - 태그 순위를 정한다. 추천(= metadata 가 있는 엄격 semver) → semver dev 태그 → 나머지
//     알파벳순이다(imageTagRanking.ts 참조).
//   - 1위 태그를 자동 선택하고 그 metadata 를 불러온다.
//   - 학습 Dataset의 이름과 Embodiment로 TASK·SIGMA_HAND_VERSION 기본값을 정한다.

// Isaac 시뮬레이션 평가에 쓰는 이미지 종류.
export const EVAL_IMAGE_KIND = "carbonsix-isaac-sim";

// metadata 존재 여부는 태그별로만 알 수 있다(없으면 404). 그래서 모든 엄격 semver 후보를 병렬로
// 조회한다. 실제 경과 시간은 왕복 한 번이다(rules.md §7). 실패는 모두 "추천 아님"으로 본다.
// 이 함수는 reject 하지 않는다. 태그별 결과는 api 계층이 세션 동안 캐시한다
// (IMAGE_METADATA_MAX_ENTRIES 상한). 후보는 낮은 버전부터 오므로 유력한 승자가 마지막에 들어가
// 제거되지 않는다. 실패는 캐시하지 않으므로 다시 시도하면 새로 조회한다.
async function probeMetadataTags(tags: readonly string[]): Promise<ReadonlySet<string>> {
  const results = await Promise.allSettled(
    tags.map((tag) => getTrainingScenarioImageMetadata(tag)),
  );
  return new Set(tags.filter((_, index) => results[index].status === "fulfilled"));
}

// 모달이 열릴 때 읽는 api 계층 캐시를 미리 채운다. 그래서 사용자가 Run Evaluation 을 누를 때는
// 순차 왕복이 이미 끝나 있다(rules.md §7). 순위는 probe 결과에 의존한다. 그래서 prefetch 도 같은
// 순위를 계산해 모달이 자동 선택할 태그를 정확히 미리 받는다. 실패는 무시한다. 실패한 항목은
// 캐시에서 제거되므로 모달이 스스로 다시 시도한다.
export function prefetchEvalImageParams(): void {
  void (async () => {
    const view = await listTrainingScenarioImages(EVAL_IMAGE_KIND);
    const probedOk = await probeMetadataTags(semverProbeCandidates(view.tags));
    const first = rankImageTags(view.tags, probedOk)[0];
    // 추천 승자는 자기 probe 로 이미 캐시에 있다. 폴백 태그만 미리 받는다.
    if (first !== undefined && !probedOk.has(first.tag)) {
      await getTrainingScenarioImageMetadata(first.tag);
    }
  })().catch(() => undefined);
}

function initialTaskValue(
  taskSpec: EvalParamSpec,
  datasetNames: readonly string[],
): EvalParamValue {
  return (
    suggestTaskOption(taskSpec.options?.map((option) => option.value) ?? [], datasetNames) ??
    taskSpec.default
  );
}

function sigmaHandVersionSpec(specs: readonly EvalParamSpec[]): EvalParamSpec | undefined {
  return specs.find((spec) => spec.key === "SIGMA_HAND_VERSION" && spec.type === "enum");
}

function initialSigmaHandVersion(
  spec: EvalParamSpec,
  datasetEmbodiments: readonly string[] | undefined,
): EvalParamValue {
  return datasetEmbodiments !== undefined &&
    datasetEmbodiments.length > 0 &&
    datasetEmbodiments.every((name) => /^sigma_hand_v?1\.0$/.test(name)) &&
    spec.options?.some((option) => option.value === "v1.0")
    ? "v1.0"
    : spec.default;
}

// metadata 로드와 기본값 복원이 같은 Dataset 기준으로 파라미터를 초기화한다.
function initialParamValues(
  specs: readonly EvalParamSpec[],
  datasetNames: readonly string[],
  datasetEmbodiments: readonly string[] | undefined,
): EvalParamValues {
  const values = paramDefaults(specs);
  const taskSpec = taskParamSpec(specs);
  if (taskSpec !== null) {
    values[taskSpec.key] = initialTaskValue(taskSpec, datasetNames);
  }
  const versionSpec = sigmaHandVersionSpec(specs);
  if (versionSpec !== undefined) {
    values[versionSpec.key] = initialSigmaHandVersion(versionSpec, datasetEmbodiments);
  }
  return values;
}

export type EvalImageParamsState = {
  // 이미지 태그 목록 상태.
  status: ListViewStatus;
  errorMessage: string | null;
  tags: readonly RankedImageTag[];
  selectedTag: string;
  selectTag: (tag: string) => void;
  imageUri: string | null;
  retryImages: () => void;

  // 선택한 태그의 metadata 상태.
  specsStatus: ListViewStatus;
  specsErrorMessage: string | null;
  specs: readonly EvalParamSpec[];
  retrySpecs: () => void;

  // 파라미터 폼 값.
  values: EvalParamValues;
  setValue: (key: string, value: EvalParamValue) => void;
  resetValues: () => void;
};

// 이미 학습한 Policy 또는 새로 학습할 Dataset 이름을 받는다. 이름 목록의 내용이 같으면
// 배열 참조가 바뀌어도 사용자가 고른 TASK 를 유지한다.
// Embodiment가 undefined이면 아직 확인하지 못한 상태이므로 버전 선택을 갱신하지 않는다.
export function useEvalImageParams(
  datasetNames: readonly string[],
  datasetEmbodiments?: readonly string[],
): EvalImageParamsState {
  const [status, setStatus] = useState<ListViewStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tags, setTags] = useState<readonly RankedImageTag[]>([]);
  const [selectedTag, setSelectedTag] = useState("");
  const [imagesReload, setImagesReload] = useState(0);

  const [specsStatus, setSpecsStatus] = useState<ListViewStatus>("idle");
  const [specsErrorMessage, setSpecsErrorMessage] = useState<string | null>(null);
  const [specs, setSpecs] = useState<readonly EvalParamSpec[]>([]);
  const [specsReload, setSpecsReload] = useState(0);
  const [values, setValues] = useState<EvalParamValues>({});
  const datasetNamesKey = JSON.stringify(datasetNames);
  const datasetEmbodimentsKey = JSON.stringify(datasetEmbodiments);
  const datasetContextRef = useRef({ names: datasetNames, embodiments: datasetEmbodiments });
  const appliedVersionContextRef = useRef<{ spec: EvalParamSpec; key: string } | null>(null);

  // metadata 응답을 기다리는 동안 Dataset을 바꿔도 최신 정보로 초기화한다.
  useEffect(() => {
    datasetContextRef.current = { names: datasetNames, embodiments: datasetEmbodiments };
  }, [datasetNames, datasetEmbodiments]);

  // 태그를 불러오고, 추천 후보의 metadata 를 조회한 뒤 순위를 정해 자동 선택한다.
  // 순위가 확정될 때까지 status 는 "loading" 이다. 조회 전 순서를 공개하면 틀린 목록이 잠깐
  // 보인다. 그리고 자동 선택이 바뀔 때 metadata effect 가 다시 실행된다.
  useEffect(() => {
    let disposed = false;
    setStatus("loading");
    setErrorMessage(null);
    const load = async (): Promise<void> => {
      const view = await listTrainingScenarioImages(EVAL_IMAGE_KIND);
      const probedOk = await probeMetadataTags(semverProbeCandidates(view.tags));
      if (disposed) {
        return;
      }
      const ranked = rankImageTags(view.tags, probedOk);
      setTags(ranked);
      setSelectedTag(ranked[0]?.tag ?? "");
      setStatus("success");
    };
    // reject 할 수 있는 것은 태그 목록 조회뿐이다. probe 는 allSettled 이고 실패는 "추천 아님"으로 센다.
    load().catch((error: unknown) => {
      if (disposed) {
        return;
      }
      setErrorMessage(resolveEvalLoadErrorMessage(error));
      setStatus("error");
    });
    return () => {
      disposed = true;
    };
  }, [imagesReload]);

  // 선택한 태그의 metadata 를 불러오고 그 기본값으로 폼을 초기화한다.
  useEffect(() => {
    if (selectedTag === "") {
      setSpecsStatus("idle");
      setSpecs([]);
      setValues({});
      return;
    }
    let disposed = false;
    setSpecsStatus("loading");
    setSpecsErrorMessage(null);
    getTrainingScenarioImageMetadata(selectedTag)
      .then((metadata) => {
        if (disposed) {
          return;
        }
        const nextSpecs = metadataToParamSpecs(metadata);
        const { names, embodiments } = datasetContextRef.current;
        setSpecs(nextSpecs);
        setValues(initialParamValues(nextSpecs, names, embodiments));
        setSpecsStatus("success");
      })
      .catch((error: unknown) => {
        if (disposed) {
          return;
        }
        setSpecs([]);
        setValues({});
        setSpecsErrorMessage(resolveEvalLoadErrorMessage(error));
        setSpecsStatus("error");
      });
    return () => {
      disposed = true;
    };
  }, [selectedTag, specsReload]);

  // Dataset 변경은 TASK 만 다시 고른다. Seed 등 사용자가 조정한 나머지 값은 유지한다.
  useEffect(() => {
    const taskSpec = taskParamSpec(specs);
    if (taskSpec === null) {
      return;
    }
    const task = initialTaskValue(taskSpec, datasetContextRef.current.names);
    setValues((prev) => (prev[taskSpec.key] === task ? prev : { ...prev, [taskSpec.key]: task }));
  }, [specs, datasetNamesKey]);

  useEffect(() => {
    const spec = sigmaHandVersionSpec(specs);
    if (spec === undefined || datasetEmbodimentsKey === undefined) return;
    // 상세 재조회가 끝나도 train Embodiment가 같으면 수동 선택을 유지한다.
    const applied = appliedVersionContextRef.current;
    if (applied?.spec === spec && applied.key === datasetEmbodimentsKey) return;
    appliedVersionContextRef.current = { spec, key: datasetEmbodimentsKey };
    const version = initialSigmaHandVersion(spec, datasetContextRef.current.embodiments);
    setValues((prev) => (prev[spec.key] === version ? prev : { ...prev, [spec.key]: version }));
  }, [specs, datasetEmbodimentsKey]);

  const setValue = useCallback((key: string, value: EvalParamValue) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  // 기본값 복원에도 현재 Dataset의 TASK·SIGMA_HAND_VERSION 추천을 적용한다.
  const resetValues = useCallback(() => {
    if (datasetEmbodiments === undefined) appliedVersionContextRef.current = null;
    setValues(initialParamValues(specs, datasetNames, datasetEmbodiments));
  }, [specs, datasetNames, datasetEmbodiments]);

  // 태그와 metadata 는 api 계층이 세션 동안 캐시한다. 그래서 다시 시도할 때는 캐시를 먼저 지워야
  // 한다. 지우지 않으면 서버에 요청하지 않고 같은 실패 상태를 반복한다. 그리고 새로 push 한
  // 이미지 태그가 전체 새로고침 전까지 보이지 않는다.
  const retryImages = useCallback(() => {
    invalidateTrainingEvalCatalogs();
    setImagesReload((key) => key + 1);
  }, []);
  const retrySpecs = useCallback(() => {
    invalidateTrainingEvalCatalogs();
    setSpecsReload((key) => key + 1);
  }, []);

  const imageUri = useMemo(
    () => tags.find((entry) => entry.tag === selectedTag)?.imageUri ?? null,
    [tags, selectedTag],
  );

  return {
    status,
    errorMessage,
    tags,
    selectedTag,
    selectTag: setSelectedTag,
    imageUri,
    retryImages,
    specsStatus,
    specsErrorMessage,
    specs,
    retrySpecs,
    values,
    setValue,
    resetValues,
  };
}
